import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, FolderTree, Layers, Package } from 'lucide-react';
import { ExpenseCategoryBreakdown } from '@/hooks/finance/useCashOutflowReport';
import { formatCurrency } from '@/lib/currency';

interface ExpenseCategoryBreakdownCardProps {
  expenseCategoryBreakdown: ExpenseCategoryBreakdown[];
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  operating: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30' },
  investing: { bg: 'bg-violet-500/10', text: 'text-violet-600', border: 'border-violet-500/30' },
  financing: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30' },
  default: { bg: 'bg-slate-500/10', text: 'text-slate-600', border: 'border-slate-500/30' },
};

const COST_GROUP_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  COGS: { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/30' },
  SGA: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/30' },
  default: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-muted' },
};

export const ExpenseCategoryBreakdownCard: React.FC<ExpenseCategoryBreakdownCardProps> = ({
  expenseCategoryBreakdown,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleCategory = (catId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const getCategoryColors = (code: string) => {
    const lowerCode = code.toLowerCase();
    if (lowerCode.includes('operating') || lowerCode.includes('opex')) return CATEGORY_COLORS.operating;
    if (lowerCode.includes('investing') || lowerCode.includes('capex')) return CATEGORY_COLORS.investing;
    if (lowerCode.includes('financing')) return CATEGORY_COLORS.financing;
    return CATEGORY_COLORS.default;
  };

  const getGroupColors = (code: string) => {
    if (code.startsWith('COGS')) return COST_GROUP_COLORS.COGS;
    if (code.startsWith('SGA')) return COST_GROUP_COLORS.SGA;
    return COST_GROUP_COLORS.default;
  };

  const totalAmount = expenseCategoryBreakdown.reduce((sum, c) => sum + c.value, 0);

  if (expenseCategoryBreakdown.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            Expenses by Category Hierarchy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            No expense category data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            Expenses by Category Hierarchy
          </CardTitle>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-xl font-bold">{formatCurrency(totalAmount)}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {expenseCategoryBreakdown.map(category => {
          const catColors = getCategoryColors(category.code);
          const isCatExpanded = expandedCategories.has(category.id);
          const catPercentage = totalAmount > 0 ? ((category.value / totalAmount) * 100).toFixed(1) : '0';

          return (
            <Collapsible 
              key={category.id} 
              open={isCatExpanded}
              onOpenChange={() => toggleCategory(category.id)}
            >
              <div className={`rounded-lg border-2 ${catColors.border} overflow-hidden`}>
                <CollapsibleTrigger className="w-full">
                  <div className={`${catColors.bg} p-4 flex items-center justify-between hover:opacity-90 transition-opacity`}>
                    <div className="flex items-center gap-3">
                      {isCatExpanded ? (
                        <ChevronDown className={`h-5 w-5 ${catColors.text}`} />
                      ) : (
                        <ChevronRight className={`h-5 w-5 ${catColors.text}`} />
                      )}
                      <FolderTree className={`h-5 w-5 ${catColors.text}`} />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">{category.name}</span>
                          <Badge variant="outline" className={`${catColors.text} ${catColors.border}`}>
                            {category.code}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {category.costGroups.length} cost group{category.costGroups.length !== 1 ? 's' : ''} • {category.count} transaction{category.count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-xl">{formatCurrency(category.value)}</p>
                      <p className="text-sm text-muted-foreground">{catPercentage}%</p>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="divide-y divide-border">
                    {category.costGroups.map(group => {
                      const groupColors = getGroupColors(group.code);
                      const isGroupExpanded = expandedGroups.has(group.id);
                      const groupPercentage = category.value > 0 
                        ? ((group.value / category.value) * 100).toFixed(1) 
                        : '0';

                      return (
                        <Collapsible
                          key={group.id}
                          open={isGroupExpanded}
                          onOpenChange={() => toggleGroup(group.id)}
                        >
                          <CollapsibleTrigger className="w-full">
                            <div className={`${groupColors.bg} p-3 pl-8 flex items-center justify-between hover:opacity-90 transition-opacity border-l-4 ${groupColors.border}`}>
                              <div className="flex items-center gap-3">
                                {isGroupExpanded ? (
                                  <ChevronDown className={`h-4 w-4 ${groupColors.text}`} />
                                ) : (
                                  <ChevronRight className={`h-4 w-4 ${groupColors.text}`} />
                                )}
                                <Layers className={`h-4 w-4 ${groupColors.text}`} />
                                <div className="text-left">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold">{group.name}</span>
                                    <Badge variant="outline" className={`${groupColors.text} ${groupColors.border} text-xs`}>
                                      {group.code}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {group.costPositions.length} cost position{group.costPositions.length !== 1 ? 's' : ''} • {group.count} txn
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold">{formatCurrency(group.value)}</p>
                                <p className="text-xs text-muted-foreground">{groupPercentage}%</p>
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <div className="divide-y divide-border/50">
                              {group.costPositions.map(position => {
                                const posPercentage = group.value > 0 
                                  ? ((position.value / group.value) * 100).toFixed(1) 
                                  : '0';

                                return (
                                  <div 
                                    key={position.id}
                                    className="p-3 pl-16 flex items-center justify-between bg-background hover:bg-muted/50 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <Package className="h-4 w-4 text-muted-foreground" />
                                      <div>
                                        <p className="font-medium">{position.name}</p>
                                        <p className="text-xs text-muted-foreground">{position.code}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold">{formatCurrency(position.value)}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {position.count} txn • {posPercentage}%
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}

                              {group.costPositions.length === 0 && (
                                <div className="p-3 pl-16 text-sm text-muted-foreground">
                                  No cost positions with expenses
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}

                    {category.costGroups.length === 0 && (
                      <div className="p-4 pl-12 text-sm text-muted-foreground">
                        No cost groups with expenses
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </CardContent>
    </Card>
  );
};
